import Button from '@monofront/design-system/components/Button';
import { formatDate } from '@monofront/business/utils';
import { Status, TrackingLink, CancelLink } from '@components/Order';
import { format as formatPrice } from '@monofront/business/utils';
import React from 'react';
import { IOrder, INoteAttribute } from '@monofront/types';
import RebuyCart from '@monofront/design-system/visuals/Icons/Old/RebuyCart';
import { useTranslations } from 'next-intl';
import InformativeBox from '@monofront/design-system/components/InformativeBox';
import Warning from '@monofront/design-system/visuals/Icons/Warning';

interface IOrderBodyDesktopProps {
  currentOrder: IOrder;
  showReorderModal: () => void;
  showCancelModal: () => void;
  isCancellable: boolean;
}

const OrderBodyDesktop: React.FC<IOrderBodyDesktopProps> = ({
  currentOrder,
  showReorderModal,
  showCancelModal,
  isCancellable,
}) => {
  const { name, createdAt, shippingAddress, totalPrice, noteAttributes } =
    currentOrder;

  const invoiceUrl =
    noteAttributes.find(
      (noteAttribute: INoteAttribute) => noteAttribute.name === 'invoice-url',
    )?.value || '';

  const tAccount = useTranslations('Account');
  const tCart = useTranslations('Cart');

  const orderDeliveryDate = new Date(
    currentOrder.noteAttributes.find((note) => note.name === 'delivery_date')
      ?.value ?? '',
  ).toLocaleDateString('fr');
  const orderDeliverySlot = currentOrder.noteAttributes
    .find((note) => note.name === 'delivery_time_slot')
    ?.value?.replace(/\s/g, '')
    .split('-');

  const updatedTimeSlot = currentOrder.updatedTimeSlot;

  const isTimeSlotVisible =
    (orderDeliverySlot && orderDeliverySlot.length > 0) ||
    updatedTimeSlot?.deliveryDate;

  return (
    <>
      <p className="tw-mb-2 tw-font-semibold">{tAccount('myOrder')}</p>
      <div className="tw-mb-12 tw-h-px tw-bg-blue-200" />
      <div className="">
        <div className="tw-flex tw-justify-between">
          <div className="tw-flex tw-space-x-8">
            <div>
              <p data-cs-mask>
                {tAccount('order')} {name}
              </p>
              <p>
                {tAccount('orderDate', {
                  date: formatDate(createdAt),
                })}
              </p>
            </div>

            <div className="price tw-flex tw-items-center tw-self-start">
              <p>{tCart('total')} : </p>
              <p className="tw-lf-title-sm tw-pl-1">
                {formatPrice(totalPrice)}
              </p>
            </div>
          </div>

          <div className="tw-flex tw-space-x-6">
            <div className="status">
              <Status order={currentOrder} align="right" />
            </div>

            <div className="actions">
              <Button
                onClick={showReorderModal}
                block={false}
                hasMinWidth={false}
              >
                <RebuyCart />
                {tAccount('reorder')}
              </Button>
              {isCancellable && (
                <div className="cancel-action">
                  <CancelLink order={currentOrder} onClick={showCancelModal} />
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="tw-my-12 tw-h-px tw-bg-blue-200" />
        {shippingAddress && (
          <>
            <div className="tw-flex tw-justify-between">
              <div>
                <p className="tw-mb-2 tw-font-semibold">
                  {tAccount('deliveryAddress')}
                </p>
                <ul className="tw-leading-6">
                  <li data-cs-mask>{shippingAddress.name}</li>
                  {shippingAddress.company && (
                    <li data-cs-mask>{shippingAddress.company}</li>
                  )}
                  <li data-cs-mask>{shippingAddress.address1}</li>
                  {shippingAddress.address2 && (
                    <li data-cs-mask>{shippingAddress.address2}</li>
                  )}
                  <li data-cs-mask>
                    {shippingAddress.zip} {shippingAddress.city}
                  </li>
                  <li data-cs-mask>{shippingAddress.country}</li>
                  <li data-cs-mask>{shippingAddress.phone}</li>
                </ul>
              </div>
              <div className="tracking-container">
                <TrackingLink order={currentOrder} />

                {invoiceUrl.length > 0 && (
                  <a
                    href={invoiceUrl}
                    className="invoice-link tw-lf-link-md"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {tAccount('invoice')}
                  </a>
                )}
              </div>
            </div>

            {isTimeSlotVisible && (
              <>
                <div className="tw-mt-12 tw-flex tw-flex-col tw-space-y-2">
                  {updatedTimeSlot?.deliveryDate && (
                    <p className="tw-font-semibold">{tAccount('chosenSlot')}</p>
                  )}
                  {orderDeliveryDate && !updatedTimeSlot?.deliveryDate && (
                    <p
                      data-testid="TimeSlot-default"
                      className="tw-font-semibold"
                    >
                      {tAccount('selectedSlot')}
                    </p>
                  )}
                  <p data-testid="TimeSlot-modified">
                    {tAccount('timeSlot', {
                      date: orderDeliveryDate,
                      startTime: orderDeliverySlot?.[0],
                      endTime: orderDeliverySlot?.[1],
                    })}
                  </p>
                </div>
                {updatedTimeSlot?.deliveryDate && (
                  <div
                    className="tw-mb-12 tw-mt-4"
                    data-testid="TimeSlot-informative-box"
                  >
                    <InformativeBox
                      icon={<Warning />}
                      backgroundColor="#FFE7DA"
                    >
                      <div className="flex tw-ml-3 tw-flex-col tw-space-y-1">
                        <p className="tw-font-semibold">
                          {tAccount('timeSlotUpdated')}
                        </p>
                        <p>
                          {tAccount('deliverySlot', {
                            date: updatedTimeSlot.deliveryDate,
                            startTime: updatedTimeSlot.startTime,
                            endTime: updatedTimeSlot.endTime,
                          })}
                        </p>
                      </div>
                    </InformativeBox>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
      <style jsx>{`
        .tracking-container {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          grid-column: 4;
          height: max-content;
        }

        .invoice-link {
          margin-left: var(--space-4);
        }

        .status {
          justify-self: center;
        }

        .actions {
          display: flex;
          align-items: center;
          height: max-content;
          justify-content: flex-end;
        }

        .label {
          font-family: var(--font-family-1);
        }

        .actions > :global(button) {
          display: flex;
          font-size: var(--font-size-2);
          justify-content: center;
          align-items: center;
        }

        .actions > :global(button > svg) {
          margin-right: var(--space-3);
        }

        .cancel-action {
          margin-left: var(--space-3);
        }

        .title {
          padding-left: var(--space-4);
        }

        .savings {
          color: var(--clr-tertiary-1);
        }

        .price > p > span {
          font-family: var(--font-family-1);
        }

        @media (max-width: 1200px) {
          .actions {
            flex-direction: column;
            align-items: flex-end;
          }

          .cancel-action {
            margin-top: var(--space-2);
          }
        }
      `}</style>
    </>
  );
};

export default OrderBodyDesktop;
